import { useState, useCallback, useMemo, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";

import FormFieldTags from "../input/FormFieldTags";
import TaskAssignments from "./TaskAssignments";
import DataTable from "../core/DataTable.jsx";
import PropTypes from "prop-types";
import Badge from "../core/Badge";

import { errorToast } from "../../util/toastNotifications";
import { useAppData } from "../../context/AppDataContext";
import { truncateName } from "../../util/stringUtils";

const TaskTable = ({
  data,
  loading,
  onDataChange,
  onTaskClick,
  showProjectColumn,
  showToggleBtn,
  onSelectionChange = () => {},
  recordsData,
  onPageChange,
  baseUrl,
  setSortParams,
  sortParams,
  task,
  resetSelectionKey,
}) => {
  const history = useHistory();
  const { api } = useAppData();

  const [selectedTaskId, setSelectedTaskId] = useState([]);
  const isSortedManually = setSortParams ? true : false;

  const PRIORITY_COLORS = {
    low: "#00f30f",
    medium: "#d1a000",
    high: "#f97316",
    urgent: "#ef4444",
  };

  const normalizedData = useMemo(() => {
    return data.map((task) => {
      let assignments = [];
      if (task.assignments) {
        assignments = task.assignments;
      } else if (task.assigned_users) {
        assignments = task.assigned_users.map((user) => ({
          user_id: user.user_id,
          user: user,
        }));
      }

      return {
        ...task,
        assignments,
      };
    });
  }, [data]);

  useEffect(() => {
    onSelectionChange(selectedTaskId);
  }, [selectedTaskId]);

  useEffect(() => {
    setSelectedTaskId([]);
  }, [resetSelectionKey]);

  const handleToggleSelect = (taskId) => {
    setSelectedTaskId((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleCellClick = useCallback(
    (row) => {
      if (onTaskClick) {
        onTaskClick(row.task_id);
      } else {
        history.push(`/task/${row.task_id}`);
      }
    },
    [history, onTaskClick]
  );

  const columns = [
    ...(showToggleBtn
      ? [
          {
            name: " ",
            id: "checkbox",
            width: "26px",
            sortable: false,
            cell: (row) => {
              const isSelected = selectedTaskId.includes(row.task_id);
              return (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleSelect(row.task_id)}
                  className="row-checkbox"
                />
              );
            },
          },
        ]
      : []),
    {
      name: "",
      id: "drag_handle",
      width: "24px",
      sortable: false,
      cell: (row, options = {}) => (
        <button
          type="button"
          className="drag-handle"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Drag ${row.name}`}
          {...(options.dragHandleProps || {})}
        >
          ⋮⋮
        </button>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div
          className="cursor-pointer text-primary truncate"
          title={row.name}
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(row);
          }}
        >
          {row.name}
        </div>
      ),
    },
    ...(showProjectColumn
      ? [
          {
            name: "Project",
            selector: (row) => row.project.name,
            sortable: true,
            cell: (row) => (
              <div
                className="cursor-pointer text-primary truncate"
                title={row.project.name}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCellClick(row);
                }}
              >
                <Link
                  className="table-link truncate"
                  to={`/project/${row.project.project_id}`}
                >
                  {row.project.name}
                </Link>
              </div>
            ),
          },
        ]
      : []),
    {
      name: "Status",
      id: "task_status",
      selector: (row) => {
        if (typeof row.status === "string") {
          return row.status;
        } else if (
          row.status &&
          typeof row.status === "object" &&
          row.status.name
        ) {
          return row.status.name;
        }
        return "No Status";
      },
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const label = (row) => {
          if (typeof row.status === "string") return row.status;
          if (row.status?.name) return row.status.name;
          return "No Status";
        };
        return label(rowA).localeCompare(label(rowB));
      },
      cell: (row) => (
        <div
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(row);
          }}
        >
          <Badge type="status" status={row.status} color={row.status_color} />
        </div>
      ),
    },
    {
      name: "Priority",
      id: "priority",
      selector: (row) => row.priority || "none",
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const priorityOrder = {
          urgent: 1,
          high: 2,
          medium: 3,
          none: 4,
          low: 5,
        };

        const priorityA = rowA.priority?.toLowerCase() || "none";
        const priorityB = rowB.priority?.toLowerCase() || "none";

        const pDiff = priorityOrder[priorityA] - priorityOrder[priorityB];
        if (pDiff !== 0) return pDiff;

        const orderA = rowA.backlog_order ?? 0;
        const orderB = rowB.backlog_order ?? 0;
        if (orderA !== orderB) return orderA - orderB;

        return (rowA.name || "").localeCompare(rowB.name || "");
      },
      cell: (row) => (
        <div
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(row);
          }}
        >
          <Badge
            type="priority"
            status={
              row.priority.slice(0, 1).toUpperCase() + row.priority.slice(1)
            }
          />
        </div>
      ),
    },
    {
      name: "Assigned",
      selector: (row) =>
        row.assignments
          ?.map((user) => `${user.user.first_name} ${user.user.last_name}`)
          .join(", ") || "-",
      sortable: !isSortedManually,
      cell: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <TaskAssignments
            taskId={row.task_id}
            task={row}
            assignments={row.assignments || []}
            readOnly={false}
            onAssignmentsChange={(newAssignments) => {
              if (onDataChange) {
                const updatedData = normalizedData.map((task) =>
                  task.task_id === row.task_id
                    ? { ...task, assignments: newAssignments }
                    : task
                );
                onDataChange(updatedData);
              }
            }}
          />
        </div>
      ),
    },
    {
      name: "Blocked",
      id: "block",
      sortable: true,
      selector: (row) => {
        const hasBlocked = row.tags?.some(
          (tag) => tag.name?.toLowerCase() === "blocked"
        );
        const hasBlocking = row.tags?.some(
          (tag) => tag.name?.toLowerCase() === "blocking"
        );
        if (hasBlocked && hasBlocking) return "Blocked, Blocking";
        if (hasBlocked) return "Blocked";
        if (hasBlocking) return "Blocking";
        return "-";
      },
      cell: (row) => {
        const hasBlocked = row.tags?.some(
          (tag) => tag.name?.toLowerCase() === "blocked"
        );
        const hasBlocking = row.tags?.some(
          (tag) => tag.name?.toLowerCase() === "blocking"
        );
        return (
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleCellClick(row);
            }}
          >
            {hasBlocked && (
              <span
                className="badge badge-danger"
                style={{ marginRight: hasBlocking ? "6px" : 0 }}
              >
                Blocked
              </span>
            )}
            {hasBlocking && (
              <span className="badge badge-danger">Blocking</span>
            )}
            {!hasBlocked && !hasBlocking && <span>-</span>}
          </div>
        );
      },
    },
    {
      name: "Tags",
      selector: (row) =>
        row.tags?.map((tag) => truncateName(tag.name)).join(", ") || "-",
      sortable: !isSortedManually,
      cell: (row) => (
        <FormFieldTags
          onClick={(e) => e.stopPropagation()}
          hideLabel
          selectedTags={row.tags || []}
          handleTagRemove={async (tagId, tag) => {
            try {
              const tagName = (tag?.name || "").toLowerCase();
              if (tagName === "blocked" || tagName === "blocking") {
                const currentTask = await api.task.getTaskById(row.task_id);
                const dependencyTaskIds = (currentTask?.dependencies || [])
                  .map((dep) => dep?.task_id)
                  .filter(Boolean);
                const dependantTaskIds = (currentTask?.dependants || [])
                  .map((dep) => dep?.task_id)
                  .filter(Boolean);

                await api.task.updateTaskDependencies(row.task_id, {
                  dependency_task_ids:
                    tagName === "blocked" ? [] : dependencyTaskIds,
                  dependant_task_ids:
                    tagName === "blocking" ? [] : dependantTaskIds,
                });
                const refreshedTask = await api.task.getTaskById(row.task_id);
                if (onDataChange) {
                  const updatedData = normalizedData.map((task) =>
                    task.task_id === row.task_id
                      ? {
                          ...task,
                          tags: refreshedTask?.tags ?? task.tags ?? [],
                          dependencies:
                            refreshedTask?.dependencies ??
                            task.dependencies ??
                            [],
                          dependants:
                            refreshedTask?.dependants ?? task.dependants ?? [],
                        }
                      : task
                  );
                  onDataChange(updatedData);
                }
                return;
              }

              const updatedTask = await api.task.toggleTag(row.task_id, tagId);
              if (onDataChange) {
                const updatedData = normalizedData.map((task) =>
                  task.task_id === row.task_id
                    ? {
                        ...task,
                        tags: updatedTask?.tags ?? task.tags ?? [],
                      }
                    : task
                );
                onDataChange(updatedData);
              }
            } catch (error) {
              errorToast(error?.message || "Failed to remove tag from task");
            }
          }}
          handleAddTagToObject={async (tagId) => {
            try {
              await api.task.toggleTag(row.task_id, tagId);

              const existingTag = (row.tags || []).find(
                (tag) => tag.tag_id === tagId
              );
              if (existingTag) {
                return;
              }

              const tagResponse = await api.tag.getTagById(tagId);
              const newTag = {
                tag_id: tagResponse.tag_id,
                name: tagResponse.name,
                color: tagResponse.color,
              };

              if (onDataChange) {
                const updatedData = normalizedData.map((task) =>
                  task.task_id === row.task_id
                    ? { ...task, tags: [...(task.tags || []), newTag] }
                    : task
                );
                onDataChange(updatedData);
              }
            } catch (error) {
              console.error("Error adding tag to task:", error);
              errorToast(error?.message || "Failed to add tag to task");
            }
          }}
          objectType="tasks"
          objectId={row.task_id}
          objectPk="task_id"
        />
      ),
    },
    {
      name: "Est",
      id: "points_estimate",
      selector: (row) => row.points_estimate || 0,
      sortable: true,
      width: 40,
      cell: (row) => (
        <div
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(row);
          }}
        >
          <span>
            {row.points_estimate && row.points_estimate == 0.5
              ? "½"
              : row.points_estimate}
          </span>
        </div>
      ),
    },
    {
      name: "Due Date",
      selector: (row) => row.due_date,
      sortable: true,
      cell: (row) => (
        <div
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(row);
          }}
        >
          <span>
            {row.due_date ? new Date(row.due_date).toLocaleDateString() : "-"}
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="task-table">
      <DataTable
        columns={columns}
        data={normalizedData}
        loading={loading}
        truncate
        highlightOnHover
        pointerOnHover
        noDataComponent="No tasks found"
        selectableRows={false}
        draggableRows
        rowIdKey="task_id"
        onRowsReorder={async (reorderedRows, dragMeta = {}) => {
          const { activeId, overId } = dragMeta;

          const activeTask = normalizedData.find(
            (t) => String(t.task_id) === String(activeId)
          );
          const overTask = normalizedData.find(
            (t) => String(t.task_id) === String(overId)
          );

          const activePriority = (activeTask?.priority || "none").toLowerCase();
          const overPriority = (overTask?.priority || "none").toLowerCase();

          if (activePriority !== overPriority) {
            return;
          }

          const samePriorityRows = reorderedRows.filter(
            (t) => (t.priority || "none").toLowerCase() === activePriority
          );
          const backlogOrderById = new Map(
            samePriorityRows.map((t, i) => [t.task_id, i])
          );
          const rowsWithOrder = reorderedRows.map((task) => ({
            ...task,
            backlog_order:
              (task.priority || "none").toLowerCase() === activePriority
                ? (backlogOrderById.get(task.task_id) ?? 0)
                : (task.backlog_order ?? 0),
          }));

          if (onDataChange) {
            onDataChange(rowsWithOrder);
          }

          const taskIdsInOrder = reorderedRows
            .filter(
              (t) => (t.priority || "none").toLowerCase() === activePriority
            )
            .map((t) => t.task_id);

          try {
            await api.task.reorderBacklog({
              priority: activePriority,
              task_ids: taskIdsInOrder,
            });
          } catch (err) {
            errorToast("Failed to save backlog order");
          }
        }}
        onPageChange={onPageChange}
        recordsData={recordsData}
        baseUrl={baseUrl}
        setSortParams={setSortParams}
        defaultSortFieldId={sortParams?.sortBy || ""}
        defaultSortAsc={sortParams?.sortDir !== "desc"}
        isSortedManually={isSortedManually}
      />
    </div>
  );
};

TaskTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      task_id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          color: PropTypes.string,
        }),
      ]),
      priority: PropTypes.string,
      assignments: PropTypes.arrayOf(
        PropTypes.shape({
          user_id: PropTypes.string.isRequired,
          user: PropTypes.shape({
            user_id: PropTypes.string.isRequired,
            first_name: PropTypes.string,
            last_name: PropTypes.string,
          }),
        })
      ),
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          tag_id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          color: PropTypes.string,
        })
      ),
      due_date: PropTypes.string,
      points_estimate: PropTypes.number,
    })
  ).isRequired,
  loading: PropTypes.bool,
  onDataChange: PropTypes.func,
  onTaskClick: PropTypes.func,
};

export default TaskTable;
